import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress,
  Paper,
  Link,
  Divider,
  Chip,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Switch
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncIcon from '@mui/icons-material/Sync';
import { getUserProfile, updateUserProfile, syncSteamLibrary, updateStatusCounts, checkSteamSyncStatus, getSteamSyncSettings, updateSteamSyncSettings, SteamSyncSettings } from '../../services/api';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon       from '@mui/icons-material/AccessTime';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineIcon     from '@mui/icons-material/ErrorOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { alpha } from '@mui/material/styles';
import Dialog from '../dialogs/Dialog';
import { useTranslation } from 'react-i18next';
import { SettingToggleCard, SettingRadioGroup } from '../common/SettingCard';

const STEAM_PRIVACY_URL = 'https://steamcommunity.com/my/edit/settings';

const SyncStatusDisplay: React.FC<{
  status: number | null;
  message: string | null;
  lastSyncTime: string | null;
  nextSyncTime: string | null;
  onOpenChecklist: () => void;
}> = ({ status, message, lastSyncTime, nextSyncTime, onOpenChecklist }) => {
  const { t } = useTranslation('settings');
  if (status === null) return null;

  let icon: React.ReactNode;
  let label: string;
  let timeLabel: string | null = null;

  switch (status) {
    case 0:
      icon = <HourglassEmptyIcon color="disabled" />;
      label = t('sync.waiting');
      timeLabel = lastSyncTime ? t('sync.lastSync', { time: lastSyncTime }) : null;
      break;
    case 1:
      icon = <AccessTimeIcon color="info" />;
      label = t('sync.queued');
      timeLabel = lastSyncTime ? t('sync.lastSync', { time: lastSyncTime }) : null;
      break;
    case 2:
      icon = <CircularProgress size={20} />;
      label = t('sync.processing');
      timeLabel = lastSyncTime ? t('sync.lastSync', { time: lastSyncTime }) : null;
      break;
    case 3:
      icon = <CheckCircleOutlineOutlinedIcon color="success" />;
      label = t('sync.completed');
      timeLabel = lastSyncTime ? t('sync.syncCompleted', { time: lastSyncTime }) : null;
      break;
    case 4:
      icon = <ErrorOutlineIcon color="error" />;
      label = t('sync.failed');
      // エラー時は30分後に再試行可能なことを表示
      if (lastSyncTime) {
        const syncDate = new Date(lastSyncTime.replace(' JST', ''));
        const retryDate = new Date(syncDate.getTime() + 1 * 9000); // 9秒後
        const now = new Date();

        if (now >= retryDate) {
          timeLabel = t('sync.retryAvailable');
        } else {
          const seconds = Math.ceil((retryDate.getTime() - now.getTime()) / 1000);
          timeLabel = t('sync.retryIn', { seconds });
        }
      }
      break;
    default:
      icon = null;
      label = '';
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
        bgcolor: status === 4 ? 'rgba(211, 47, 47, 0.04)' : 'inherit' // エラー時は薄い赤背景
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          {icon}
          <Typography variant="body2" fontWeight="500" whiteSpace="nowrap">
            {label}
          </Typography>
        </Box>
        {/* エラー時は原因が本文なので、右端の caption には出さず下にブロックで出す */}
        {message && status !== 4 && (
          <Typography variant="caption" color="text.secondary">
            {message}
          </Typography>
        )}
      </Box>

      {status === 4 && (
        <Box mt={1}>
          {message && (
            <Typography variant="body2" color="text.primary" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
              {message}
            </Typography>
          )}
          {/* 原因はほぼ全てSteam側の公開設定なので、その場で直しに行ける導線を置く */}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Link
              href={STEAM_PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 500,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('sync.openSteamPrivacy')}
              <OpenInNewIcon fontSize="small" />
            </Link>
            <Button
              onClick={onOpenChecklist}
              size="small"
              color="inherit"
              sx={{ color: 'text.secondary' }}
            >
              {t('sync.showChecklist')}
            </Button>
          </Box>
        </Box>
      )}

      {timeLabel && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            {timeLabel}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const SteamSettings: React.FC = () => {
  const { t } = useTranslation('settings');
  const [steamId, setSteamId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // メッセージ(success/error)をどのセクションのAlertに出すか。
  // 以前は本文に「同期」が含まれるかで判定していたが、英語化で破綻するため明示的に持つ。
  const [msgScope, setMsgScope] = useState<'sync' | 'repair'>('sync');
  const [hasSteamId, setHasSteamId] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const [nextSyncTime, setNextSyncTime] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [canSync, setCanSync] = useState<boolean>(true);
  const [syncStatus, setSyncStatus] = useState<number | null>(null);
  const [syncStatusMessage, setSyncStatusMessage] = useState<string | null>(null);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState<boolean>(false);
  const [syncSettings, setSyncSettings] = useState<SteamSyncSettings | null>(null);
  const [nextAutoSyncTime, setNextAutoSyncTime] = useState<string | null>(null);
  const [settingsSaving, setSettingsSaving] = useState<boolean>(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [linkMenuAnchor, setLinkMenuAnchor] = useState<null | HTMLElement>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await getUserProfile();
        if (response.data.steam_id) {
          setSteamId(response.data.steam_id);
          setHasSteamId(true);
          // プロフィール取得成功後に同期ステータスと自動同期設定をチェック
          checkSyncStatus();
          loadSyncSettings();
        }
      } catch (err) {
        setError(t('connect.profileFetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    
    // コンポーネントのアンマウント時にポーリングをクリア
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // 同期ステータスを確認する関数
  const checkSyncStatus = async () => {
    try {
      const result = await checkSteamSyncStatus();
      setSyncStatus(result.status);
      setSyncStatusMessage(result.message);
      
      if (result.next_sync_time) {
        setNextSyncTime(result.next_sync_time);
      }
      
      if (result.last_sync_at) {
        const dt = new Date(result.last_sync_at);
        const formattedLast = dt.toLocaleString('ja-JP', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        }).replace(/\//g, '-').replace(/,/g, '');
        setLastSyncTime(formattedLast);
      }
      
      // ステータスに基づいて同期ボタンの状態を更新
      setCanSync(result.can_sync && result.status !== 2); // 処理中でなく、同期可能であれば
      
      // 処理中(2)の場合、定期的にステータスを確認
      if (result.status === 2 && !pollingRef.current) {
        pollingRef.current = setInterval(checkSyncStatus, 10000); // 10秒ごとに確認
      } 
      // 処理中でなくなったらポーリングを停止
      else if (result.status !== 2 && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      
    } catch (err) {
      console.error('同期ステータスの確認に失敗:', err);
    }
  };

  // 自動同期設定を読み込む
  const loadSyncSettings = async () => {
    try {
      const result = await getSteamSyncSettings();
      setSyncSettings(result.settings);
      setNextAutoSyncTime(result.next_sync_time);
    } catch (err) {
      console.error('自動同期設定の取得に失敗:', err);
    }
  };

  // 設定を部分更新する（楽観的更新＋失敗時ロールバック）
  const handleSettingChange = async (partial: Partial<SteamSyncSettings>) => {
    if (!syncSettings) return;
    const prev = syncSettings;
    setSyncSettings({ ...syncSettings, ...partial });
    setSettingsSaving(true);
    setSettingsError(null);
    try {
      const result = await updateSteamSyncSettings(partial);
      setSyncSettings(result.settings);
    } catch (err: any) {
      setSyncSettings(prev); // ロールバック
      setSettingsError(err.message || t('steam.settingsFailed'));
    } finally {
      setSettingsSaving(false);
    }
  };


  const handleSteamAuth = () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/steam/callback?mode=link`);
    window.location.href = `${process.env.REACT_APP_API_URL || ''}/auth/steam/login/?redirect_uri=${redirectUri}`;
  };

  const handleSyncLibrary = async () => {
    setShowPrivacyDialog(true);
  };

  const handleConfirmSync = async () => {
    setShowPrivacyDialog(false);
    try {
      setSyncLoading(true);
      setMsgScope('sync');
      setError(null);
      setSuccess(null);

      const result = await syncSteamLibrary();
      setSuccess(result.message || t('steam.syncStarted'));
      
      // エラー状態の場合は同期可能フラグを true に設定
      setCanSync(result.status === 4);
      setSyncStatus(result.status);
      
      if (result.next_sync_time) {
        setNextSyncTime(result.next_sync_time);
      }
      
      // ポーリングを開始
      if (result.status === 2 && !pollingRef.current) {
        pollingRef.current = setInterval(checkSyncStatus, 10000);
      }

      // 手動同期で last_sync_at が進むため、自動同期の次回予定も更新する
      if (syncSettings) {
        loadSyncSettings();
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setNextSyncTime(err.response.data.next_sync_time);
        setError(err.response.data.message);
      } else {
        setError(err.message || t('steam.syncFailed'));
        // エラー時は同期可能フラグを true に設定
        setCanSync(true);
      }
    } finally {
      setSyncLoading(false);
    }
  };

  const handleUpdateStatusCounts = async () => {
    const now = Date.now();
    if (now - lastUpdateTime < 2000) {
      return;
    }

    try {
      setUpdateLoading(true);
      setMsgScope('repair');
      setError(null);
      setSuccess(null);
      setLastUpdateTime(now);
      
      const [result] = await Promise.all([
        updateStatusCounts(),
        new Promise(resolve => setTimeout(resolve, 2000))
      ]);
      
      setSuccess(t('connect.statusCountUpdated'));
    } catch (err: any) {
      setError(err.message || t('connect.statusCountFailed'));
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUnlinkSteam = async () => {
    if (!window.confirm(t('steam.unlinkConfirm'))) {
      return;
    }

    try {
      setLoading(true);
      setMsgScope('repair');
      setError(null);
      setSuccess(null);

      await updateUserProfile({ steam_id: null });
      setSteamId('');
      setHasSteamId(false);
      setSuccess(t('steam.unlinked'));
      
      // ポーリングを停止
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    } catch (err: any) {
      setError(err.message || t('steam.unlinkFailed'));
    } finally {
      setLoading(false);
    }
  };

  const privacyDialogContent = (
    <Box>
      <Typography variant="body1" gutterBottom>
        {t('steam.privacyIntro')}
        <Link
          href={STEAM_PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            }
          }}
        >
          {t('steam.privacyLink')}
          <OpenInNewIcon fontSize="small" />
        </Link>
      </Typography>
      <Box component="ul" sx={{ pl: 2 }}>
        <Typography component="li" variant="body2" color="text.secondary" gutterBottom>
          {t('steam.privacyItem1')}
        </Typography>
        <Typography component="li" variant="body2" color="text.secondary" gutterBottom>
          {t('steam.privacyItem2')}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
        {t('steam.privacyNote')}
      </Typography>
      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={() => setShowPrivacyDialog(false)} color="inherit" size="small">
          {t('cancel', { ns: 'common' })}
        </Button>
        <Button onClick={handleConfirmSync} variant="contained" color="primary" size="small">
          {t('steam.startSync')}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('steam.title')}
      </Typography>

      <Paper elevation={0} sx={{ mb: 3, bgcolor: "background.paper" }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('steam.desc')}
        </Typography>

        <Box sx={{ mb: 3 }}>
          {hasSteamId ? (
            <>
              <Alert
                severity="success"
                action={
                  <IconButton
                    size="small"
                    onClick={(e) => setLinkMenuAnchor(e.currentTarget)}
                    disabled={loading}
                    sx={{ color: '#ffffff' }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  maxWidth: 500,
                  background:
                    "linear-gradient(135deg, rgba(23, 26, 33, 0.95), rgba(42, 71, 94, 0.95))",
                  color: "#ffffff",
                  border: "1px solid rgba(102, 192, 244, 0.2)",
                  borderRadius: 2,
                  backdropFilter: "blur(8px)",
                  "& .MuiAlert-icon": { color: "#66c0f4" },
                  "& .MuiAlert-action": { alignItems: "center", pt: 0 },
                }}
              >
                {t('steam.linked', { id: steamId })}
              </Alert>
              <Menu
                anchorEl={linkMenuAnchor}
                open={Boolean(linkMenuAnchor)}
                onClose={() => setLinkMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => { setLinkMenuAnchor(null); handleSteamAuth(); }}
                  disabled={loading}
                >
                  {t('steam.relink')}
                </MenuItem>
                <MenuItem
                  onClick={() => { setLinkMenuAnchor(null); handleUnlinkSteam(); }}
                  disabled={loading}
                  sx={{ color: 'error.main' }}
                >
                  {t('steam.unlink')}
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={handleSteamAuth}
                disabled={loading}
                size="small"
                startIcon={
                  <img 
                    src="/icons/service/source/steam_.svg" 
                    alt="Steam" 
                    style={{ width: '20px', height: '20px', filter: 'invert(1)' }}
                  />
                }
                sx={{ 
                  mb: 2,
                  background: 'linear-gradient(135deg, rgba(23, 26, 33, 0.95), rgba(42, 71, 94, 0.95))',
                  color: '#ffffff',
                  border: '1px solid rgba(102, 192, 244, 0.2)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(23, 26, 33, 1), rgba(42, 71, 94, 1))',
                    border: '1px solid rgba(102, 192, 244, 0.3)',
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, rgba(23, 26, 33, 0.3), rgba(42, 71, 94, 0.3))',
                    color: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                {t('steam.authButton')}
              </Button>
            </>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1">
              Steam Sync 
            </Typography>
            <Chip 
              label="v1.0.1" 
              size="small" 
              color="default" 
              variant="outlined" 
              sx={{ 
                ml: 1, 
                height: 18, 
                borderColor: 'rgba(102, 192, 244, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                fontSize: '11px',
                color: 'rgba(102, 192, 244, 1)',
              }} 
            />
          </Box>
          <Typography variant="body2" paragraph color="text.secondary">
            {t('steam.syncDesc')}
          </Typography>

          <SyncStatusDisplay
            status={syncStatus}
            message={syncStatusMessage}
            lastSyncTime={lastSyncTime}
            nextSyncTime={nextSyncTime}
            onOpenChecklist={handleSyncLibrary}
          />

          {(success && msgScope === 'sync') && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {(error && msgScope === 'sync') && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSyncLibrary}
            disabled={syncLoading || !hasSteamId || !canSync}
            startIcon={syncLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            sx={{ mt: 1 }}
            size="small"
          >
            {syncStatus === 2 ? t('steam.syncing') : t('steam.syncNow')}
          </Button>
        </Box>

        {/* 同期設定（連携済み かつ 設定取得済みのときのみ表示）
            これらは手動・自動どちらの同期にも適用される「同期自体の設定」。
            auto_sync は実行スケジュール（1日1回自動実行）のON/OFFのみを担う。 */}
        {hasSteamId && syncSettings && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {t('steam.autoSyncTitle')}
              </Typography>

              {settingsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {settingsError}
                </Alert>
              )}

              {/* 自動同期：全設定の親となるマスターカード（周期実行のスケジューリング） */}
              <SettingToggleCard
                variant="master"
                checked={syncSettings.auto_sync}
                onToggle={(v) => handleSettingChange({ auto_sync: v })}
                disabled={settingsSaving}
                icon={
                  <AutorenewIcon
                    sx={{
                      fontSize: 24,
                      '@keyframes steamSyncSpin': {
                        from: { transform: 'rotate(0deg)' },
                        to: { transform: 'rotate(360deg)' },
                      },
                      animation: syncSettings.auto_sync ? 'steamSyncSpin 4s linear infinite' : 'none',
                    }}
                  />
                }
                title={t('steam.autoSyncCard')}
                // accentBadge={
                //   <Chip
                //     icon={<ScheduleIcon sx={{ fontSize: 13 }} />}
                //     label="毎日 1回"
                //     size="small"
                //     sx={{
                //       height: 20,
                //       fontSize: 11,
                //       fontWeight: 600,
                //       bgcolor: (theme) => alpha(theme.palette.primary.main, syncSettings.auto_sync ? 0.18 : 0.1),
                //       color: 'primary.main',
                //       '& .MuiChip-icon': { color: 'primary.main', ml: 0.5 },
                //     }}
                //   />
                // }
                description={t('steam.autoSyncDesc')}
              >
                <Collapse in={syncSettings.auto_sync}>
                  <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    {nextAutoSyncTime && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, my: 1.5 }}>
                        <EventRepeatIcon sx={{ fontSize: 15, color: 'primary.main' }} />
                        <Typography variant="caption" color="text.secondary">
                          {t('steam.nextSyncPrefix')}{' '}
                          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {nextAutoSyncTime}
                          </Box>{' '}
                          {t('steam.nextSyncSuffix')}
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                      {t('steam.syncMode')}
                    </Typography>
                    <SettingRadioGroup
                      value={syncSettings.organize_level}
                      onChange={(v) => handleSettingChange({ organize_level: v as 'hybrid' | 'full' })}
                      options={[
                        {
                          value: 'hybrid',
                          label: t('steam.hybridLabel'),
                          description: t('steam.hybridDesc'),
                        },
                        {
                          value: 'full',
                          label: t('steam.fullLabel'),
                          description: t('steam.fullDesc'),
                        },
                      ]}
                    />
                    {/* ハイブリッドのサブ設定。モード2択とは別物なので字下げ＋左罫線でネスト表現 */}
                    {syncSettings.organize_level === 'hybrid' && (
                      <Box
                        sx={{
                          mt: 0.75,
                          ml: 0.5,
                          pl: 1.25,
                          borderLeft: '2px solid',
                          borderColor: 'divider',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 0.5,
                        }}
                      >
                        <Switch
                          size="small"
                          checked={syncSettings.auto_full_when_dormant}
                          onChange={(e) => handleSettingChange({ auto_full_when_dormant: e.target.checked })}
                          disabled={settingsSaving}
                          sx={{ flexShrink: 0, transform: 'scale(0.85)', mt: -0.25 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.4 }}>
                            {t('steam.dormantTitle')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3, fontSize: '0.68rem' }}>
                            {t('steam.dormantDesc')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </SettingToggleCard>

              {/* 取り込み設定（additive・手動/自動どちらにも適用） */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 2, mb: 1, fontWeight: 600 }}
              >
                {t('steam.importTitle')}
              </Typography>

              <SettingToggleCard
                checked={syncSettings.sync_wishlist}
                onToggle={(v) => handleSettingChange({ sync_wishlist: v })}
                disabled={settingsSaving}
                icon={<BookmarkBorderIcon sx={{ fontSize: 20 }} />}
                title={t('steam.wishlistTitle')}
                description={t('steam.wishlistDesc')}
              />

              {/* 閾値：「プレイ済み」と判定するプレイ時間（新規分類＋フル整理で共用） */}
              <Box sx={{ mt: 1.5, p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t('steam.thresholdTitle')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    type="number"
                    size="small"
                    value={syncSettings.promote_threshold_hours}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (syncSettings) {
                        setSyncSettings({ ...syncSettings, promote_threshold_hours: isNaN(v) ? 0 : v });
                      }
                    }}
                    onBlur={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v) && v >= 1) {
                        handleSettingChange({ promote_threshold_hours: v });
                      }
                    }}
                    inputProps={{ min: 1, style: { width: 56, textAlign: 'right' } }}
                    disabled={settingsSaving}
                  />
                  <Typography variant="body2" color="text.secondary">{t('steam.thresholdUnit')}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {t('steam.thresholdDesc')}
                </Typography>
              </Box>

            </Box>
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {t('connect.repairTitle')}
          </Typography>

          <Typography variant="body2" paragraph color="text.secondary">
            {t('connect.repairDesc')}
          </Typography>

          {(success && msgScope === 'repair') && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {(error && msgScope === 'repair') && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleUpdateStatusCounts}
            disabled={updateLoading || Date.now() - lastUpdateTime < 2000}
            startIcon={updateLoading ? <CircularProgress size={20} /> : null}
            sx={{
              mt: 1,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "action.hover",
                borderColor: "text.secondary",
              },
            }}
            size="small"
          >
            {updateLoading ? t('connect.updating') : t('connect.repairButton')}
          </Button>
        </Box>
      </Paper>

      <Dialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        title={t('steam.privacyTitle')}
        content={privacyDialogContent}
        showCloseButton={false}
        width={480}
      />
    </Box>
  );
};

export default SteamSettings; 